import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// project-imports
import { readActiveAccount } from 'utils/activeAccount';
import { isPublicPath, safeReturnTo } from 'utils/return-to';
import { requestStepUp, StepUpRequiredError } from 'utils/step-up';

// ==============================|| AXIOS - RAYA CONSOLE API ||============================== //
//
// Sessions are cookies that console-api sets (`__Host-raya_console_at` and `__Host-raya_console_rt`).
// This file cannot read them and does not need to. The browser sends them; this file does the three
// things the browser does not do on its own:
//
//   1. echoes the double-submit CSRF cookie on every state-changing request,
//   2. refreshes an expired access token once, shared by all requests that hit 401 together,
//   3. turns the API's machine-readable `reason` into the right navigation or dialog,
//   4. tells the API which developer account a `/v1/dev/*` call is for (`X-Developer-Id`).

export const CSRF_COOKIE = 'raya_console_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const ACCOUNT_HEADER = 'X-Developer-Id';

export type RequestOptions = AxiosRequestConfig & { skipRecovery?: boolean };

/** For calls that must never refresh, retry or redirect: logout, and the refresh itself. */
export const SILENT: RequestOptions = { skipRecovery: true };

/** The request config as the interceptors see it, with the flags they set on it. */
type RecoverableRequest = InternalAxiosRequestConfig & {
  skipRecovery?: boolean;
  authRetried?: boolean;
  raceRetried?: boolean;
};

/** What every `catch` receives. The envelope is flattened so pages read `err.message` directly. */
export type ApiError = {
  status?: number;
  code?: string;
  reason?: string;
  message: string;
  errors?: { attribute: string; message: string }[];
};

export const readCookie = (name: string): string => {
  const prefix = `${name}=`;
  const part = document.cookie.split('; ').find((item) => item.startsWith(prefix));

  return part ? decodeURIComponent(part.slice(prefix.length)) : '';
};

const axiosServices = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

axiosServices.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();

  if (method !== 'get' && method !== 'head' && method !== 'options') {
    // Double-submit: the server compares this header with the non-HttpOnly cookie of the same name.
    config.headers[CSRF_HEADER] = readCookie(CSRF_COOKIE);
  }

  // The account the console is working as. Hub reads it on the account-level list and create endpoints and
  // ignores it where the resource itself names the account. A caller that sets the header itself wins.
  if ((config.url ?? '').startsWith('/v1/dev/') && !config.headers[ACCOUNT_HEADER]) {
    const account = readActiveAccount();

    if (account) config.headers[ACCOUNT_HEADER] = account;
  }

  return config;
});

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

type ErrorBody = { message?: unknown; code?: unknown; reason?: unknown; errors?: unknown };

const readErrorBody = (data: unknown): ErrorBody => {
  if (!data || typeof data !== 'object') return {};

  const record = data as Record<string, unknown>;
  const payload = record.payload && typeof record.payload === 'object' ? (record.payload as ErrorBody) : {};

  // Top-level keys win. The error contract is flat; the envelope form is accepted as well.
  return { ...payload, ...(record as ErrorBody) };
};

const toApiError = (error: AxiosError): ApiError => {
  const body = readErrorBody(error.response?.data);

  return {
    status: error.response?.status,
    code: typeof body.code === 'string' ? body.code : undefined,
    reason: typeof body.reason === 'string' ? body.reason : undefined,
    message: typeof body.message === 'string' && body.message !== '' ? body.message : error.message,
    errors: Array.isArray(body.errors) ? (body.errors as ApiError['errors']) : undefined
  };
};

/**
 * Whether a 2xx response is something other than this API talking.
 *
 * The case that matters: a request that missed the API and fell through to the SPA fallback comes
 * back 200 with index.html. Nothing looks broken, and every caller destructures `undefined`.
 * Blobs and empty bodies are not flagged.
 */
const isMisrouted = (response: AxiosResponse): boolean => {
  const responseType = response.config.responseType;

  if (responseType === 'blob' || responseType === 'arraybuffer') return false;

  if (typeof response.data === 'string') return response.data.trim() !== '';

  return String(response.headers?.['content-type'] ?? '').includes('text/html');
};

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

/** Sends the browser to the login page, keeping the current path as `returnTo`. */
export const redirectToLogin = (): void => {
  const { pathname, search } = window.location;

  if (isPublicPath(pathname)) return;

  const returnTo = safeReturnTo(`${pathname}${search}`);

  window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
};

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

// One refresh in flight, no matter how many requests hit 401 at once. Refresh tokens rotate and
// reuse is treated as replay: two concurrent refreshes would revoke the session the first one just
// issued. The server's 15 s grace window covers a stray second call, but one call is the goal.
let refreshing: Promise<unknown> | null = null;

const refreshSession = (): Promise<unknown> => {
  if (!refreshing) {
    refreshing = axiosServices.post('/v1/auth/refresh', undefined, SILENT).finally(() => {
      refreshing = null;
    });
  }

  return refreshing;
};

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

axiosServices.interceptors.response.use(
  // The API wraps every success in `{ success, payload }`. Unwrapping here keeps the envelope out of
  // every page; a change to it is one edit in this file.
  (response) => {
    const data = response.data;

    if (data && typeof data === 'object' && 'success' in data && 'payload' in data) {
      response.data = data.payload;

      return response;
    }

    if (isMisrouted(response)) {
      const misrouted: ApiError = {
        status: response.status,
        code: 'not_an_api_response',
        message: `Response to ${response.config.url ?? 'request'} did not come from the API. Check REACT_APP_API_URL.`
      };

      return Promise.reject(misrouted);
    }

    return response;
  },

  async (error: AxiosError) => {
    const request = error.config as RecoverableRequest | undefined;
    const status = error.response?.status;
    const apiError = toApiError(error);
    const reason = apiError.reason;

    if (!request || request.skipRecovery) {
      return Promise.reject(apiError);
    }

    // The admin is signed in but lacks a fresh confirmation. The dialog takes over and the caller
    // stays quiet, so the typed error is rejected instead of a generic one.
    if (status === 403 && reason === 'step_up_required') {
      requestStepUp();

      return Promise.reject(new StepUpRequiredError());
    }

    // Another request rotated the session between our send and the answer. The new cookies are in
    // place, so the original call is sent again, once.
    if (status === 409 && reason === 'refresh_race' && !request.raceRetried) {
      request.raceRetried = true;

      if (refreshing) {
        await refreshing.catch(() => undefined);
      }

      return axiosServices(request);
    }

    if (status === 401 && reason === 'reauth_required') {
      redirectToLogin();

      return Promise.reject(apiError);
    }

    // `unauthenticated` is the normal "access token expired" answer. A 401 with no reason is handled
    // the same way: one refresh is cheap, a spurious sign-in is not.
    if (status === 401 && (reason === undefined || reason === 'unauthenticated') && !request.authRetried) {
      // Without the CSRF cookie there is no session to refresh: the refresh POST would be refused with 403
      // anyway. Treat it as signed out at once, instead of a pointless round trip.
      if (readCookie(CSRF_COOKIE) === '') {
        redirectToLogin();

        return Promise.reject(apiError);
      }

      request.authRetried = true;

      try {
        await refreshSession();
      } catch {
        redirectToLogin();

        return Promise.reject(apiError);
      }

      return axiosServices(request);
    }

    // 403 `forbidden` is deliberately not a session problem. Sending the admin to the login page would
    // loop: signing in again changes nothing about the role.
    return Promise.reject(apiError);
  }
);

export default axiosServices;
